/**
 * Custom App Bar
 *
 * v3 app bar (brand rule "1a"): the product name is NOT repeated here — it
 * lives once as the text lockup at the top of the rail (CustomMenu). This
 * bar carries the section switcher on the left (the screens of the section
 * the rail selected — see SectionSwitcher) and the environment pill on the
 * right. Pattern A: on desktop it is absolutely positioned over the content
 * column only (left = rail width, width = viewport − rail), so it never
 * overlaps the rail. The user profile lives in the rail's profile card
 * (single-place rule) — no app-bar user menu.
 */

import React from 'react';
import { AppBar, TitlePortal, LocalesMenuButton, useSidebarState, useTranslate, usePermissions } from 'react-admin';
import { Box, Button, useMediaQuery, useTheme } from '@mui/material';
import SwitchAccountRoundedIcon from '@mui/icons-material/SwitchAccountRounded';
import PhoneRoundedIcon from '@mui/icons-material/PhoneRounded';
import { useLocation } from 'react-router-dom';
import { getImpersonation, stopImpersonation } from '@/utils/impersonation';
import { APP_NAME } from '@/auth/authConfig';
import { getRoleId } from '@/utils/auth';
import { ROLE_IDS } from '@/config/constants';
import { getVisibleSections, resolveRoute } from './navigation';
import { SectionSwitcher } from './SectionSwitcher';

const IS_PRODUCTION = import.meta.env.MODE === 'production';

// qr-phone-caller is built with base '/caller-beta/' only on the production
// hosts; everywhere else (dev.qr-phone.com, localhost, ...) it is served from
// the root.
const CALLER_BETA_HOSTS = new Set(['qr-phone.com', 'www.qr-phone.com']);

// Exported so other staff-facing surfaces (e.g. the dashboard's desktop-window
// card) can link to the softphone without duplicating this hostname logic.
export const getStaffCallerUrl = () => {
  const { protocol, host, hostname } = window.location;
  const base = CALLER_BETA_HOSTS.has(hostname) ? '/caller-beta/' : '/';
  return `${protocol}//${host}${base}#/staff`;
};

export const CustomAppBar: React.FC = () => {
  const theme = useTheme();
  const translate = useTranslate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [open] = useSidebarState();
  // Re-render signal once auth resolves; the rail uses the same one so both
  // read the same visible-section set.
  usePermissions();

  const current = resolveRoute(location.pathname, getVisibleSections());
  const impersonation = getImpersonation();
  // Daily-use action for staff (role 3): open the softphone to receive calls.
  // One-time setup (install/auto-start) lives in the dashboard's desktop-window
  // card instead, so this button stays staff-only to avoid duplicating that
  // entry point for other roles.
  const staff = getRoleId() === ROLE_IDS.STAFF;

  // Keep the browser tab title in step with the active screen, so tabs and
  // history entries are self-describing. Falls back to the plain brand title
  // (the static index.html default) when the route is outside the menu.
  React.useEffect(() => {
    if (!current) {
      document.title = `${APP_NAME} - Administración`;
      return;
    }
    // A collection view is the item's own route (e.g. `/clients`), as opposed
    // to a single record or a create/edit form (`/clients/create`, `/clients/1`).
    // Resource names pluralize (`Cliente |||| Clientes`), so lists read plural
    // while record/form views read singular.
    const isCollectionView =
      current.item.to === '/' ? location.pathname === '/' : location.pathname === current.item.to;
    const label = translate(current.item.labelKey, {
      smart_count: isCollectionView ? (current.item.smartCount ?? 1) : 1,
    });
    document.title = `${label} · ${APP_NAME} - Administración`;
  }, [current, location.pathname, translate]);

  return (
    <AppBar
      userMenu={false}
      toolbar={<LocalesMenuButton />}
      elevation={0}
      sx={{
        backgroundColor: (theme.palette.background as any).appbar || theme.palette.background.default,
        color: theme.palette.text.primary,
        height: '52px',
        minHeight: '52px',
        // Desktop: the bar is "docked" over the content column only —
        // absolute, starting at the rail's right edge (which shifts when the
        // rail collapses) and running to the viewport right. Mobile:
        // full-width fixed over the overlay rail.
        position: isMobile ? 'fixed' : 'absolute',
        top: 0,
        left: isMobile ? 0 : open ? theme.custom.menu.openWidth : theme.custom.menu.closedWidth,
        width: isMobile
          ? '100%'
          : open
            ? `calc(100% - ${theme.custom.menu.openWidth})`
            : `calc(100% - ${theme.custom.menu.closedWidth})`,
        zIndex: isMobile ? 1200 : theme.zIndex.appBar,
        transition: 'left 0.2s ease-in-out, width 0.2s ease-in-out',
        '& .MuiToolbar-root': {
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          minHeight: '52px !important',
          height: '52px',
          padding: { xs: '0 8px', sm: '0 22px' },
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0, gap: 1.5 }}>
        {current && <SectionSwitcher section={current.section} current={current.item} />}

        <TitlePortal sx={{ display: 'none' }} />
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
        {staff && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<PhoneRoundedIcon sx={{ fontSize: '16px' }} />}
            onClick={() => window.open(getStaffCallerUrl(), '_blank', 'noopener,noreferrer')}
            sx={{
              height: '30px',
              fontSize: '0.75rem',
              fontWeight: 600,
              textTransform: 'none',
              whiteSpace: 'nowrap',
            }}
          >
            {translate('common.staffCaller.button', { _: 'Recibir llamadas' })}
          </Button>
        )}
        {impersonation && (
          // Always-visible safety indicator (same slot pattern as the
          // environment pill): while it shows, every request runs with the
          // impersonated user's identity and permissions, reads AND writes.
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              height: '26px',
              padding: '0 4px 0 10px',
              borderRadius: theme.custom.radius.full,
              backgroundColor: 'rgba(239,68,68,0.12)',
              color: '#b91c1c',
              fontSize: '0.75rem',
              fontWeight: 600,
              minWidth: 0,
            }}
          >
            <SwitchAccountRoundedIcon sx={{ fontSize: '16px', flexShrink: 0 }} />
            <Box
              component="span"
              sx={{
                minWidth: 0,
                maxWidth: { xs: '110px', sm: '260px' },
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {translate('common.impersonation.active', {
                name: impersonation.displayName,
                _: `Suplantando a ${impersonation.displayName}`,
              })}
            </Box>
            <Button
              size="small"
              onClick={() => {
                stopImpersonation();
                window.location.hash = '#/';
                window.location.reload();
              }}
              sx={{
                minWidth: 0,
                height: '20px',
                padding: '0 8px',
                fontSize: '0.7rem',
                fontWeight: 700,
                color: '#b91c1c',
                flexShrink: 0,
              }}
            >
              {translate('common.impersonation.exit', { _: 'Salir' })}
            </Button>
          </Box>
        )}
        {!IS_PRODUCTION && (
          <Box
            sx={{
              display: { xs: 'none', sm: 'flex' },
              alignItems: 'center',
              gap: 0.75,
              height: '26px',
              padding: '0 10px',
              borderRadius: theme.custom.radius.full,
              backgroundColor: 'rgba(251,146,60,0.15)',
              color: '#b45309',
              fontSize: '0.75rem',
              fontWeight: 600,
            }}
          >
            <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#f59e0b' }} />
            TEST ENV
          </Box>
        )}
      </Box>
    </AppBar>
  );
};

export default CustomAppBar;
